import { type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { BENEFITS } from '@/benefits-flow/engine/benefits';
import { evaluate, totalEstimatedValue } from '@/benefits-flow/engine/evaluate';
import { type ScreenerInput } from '@/benefits-flow/engine/metrics';
import { gbp, valueRange } from '@/benefits-flow/format';
import Pill from '@/benefits-flow/Pill';
import {
  type Answers,
  QUIZ_ORDER,
  buildInput,
} from '@/benefits-flow/screener/questions';
import { STATUS_TONE } from '@/benefits-flow/tones';
import {
  DELTA,
  TAP,
} from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';

// Molecules shared across the answer-mode frames. Each one appears in three or
// more frames — a proposed answer is the shape every mode terminates in, and a
// results panel is what every mode has to visibly move. Anything used once
// stays inline in its own frame file.

// A soft panel for the things the system says about itself: what it is doing,
// what it could not do, what it wants checking. Distinct from a question.
export const Note = ({
  title,
  body,
  tone = 'quiet',
  icon,
  children,
}: {
  title: string;
  body?: string;
  tone?: 'quiet' | 'working' | 'warn';
  icon?: string;
  children?: ReactNode;
}) => (
  <Stack
    sx={{
      p: 2,
      borderRadius: `${radius.md}px`,
      border: 1,
      borderColor: tone === 'warn' ? 'custom.coral.600' : 'custom.neutral.200',
      bgcolor:
        tone === 'working'
          ? 'custom.marineGreen.50'
          : tone === 'warn'
            ? 'custom.coral.50'
            : 'background.paper',
    }}
    spacing={1}
  >
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
      {icon && (
        <Box
          sx={{
            display: 'flex',
            color: tone === 'warn' ? 'custom.coral.700' : 'text.secondary',
          }}
        >
          <Icon icon={icon as never} fontSize={14} />
        </Box>
      )}
      <Typography variant='subtitle2'>{title}</Typography>
    </Stack>
    {body && (
      <Typography variant='body2' color='text.secondary'>
        {body}
      </Typography>
    )}
    {children}
  </Stack>
);
Note.displayName = 'Note';

// One answer the system is putting forward rather than asserting. The source
// line is the load-bearing part: it is what makes a proposal checkable instead
// of something the user has to take on trust.
export type ProposalState = 'open' | 'confirmed';

export const ProposalRow = ({
  question,
  value,
  said,
  saidPrefix,
  state = 'open',
}: {
  question: string;
  value: string;
  said?: string;
  saidPrefix?: string;
  state?: ProposalState;
}) => (
  <Stack
    sx={{
      p: 2,
      borderRadius: `${radius.md}px`,
      border: 1,
      borderColor:
        state === 'confirmed' ? 'custom.charcoal' : 'custom.neutral.200',
      bgcolor:
        state === 'confirmed' ? 'custom.limeGreen.100' : 'background.paper',
    }}
    spacing={0.75}
    // Charcoal rather than a darker lime: Loft's preview palette collapses the
    // lime family to one value, so lime-on-lime disappears. See tones.ts.
  >
    <Typography variant='caption' color='text.secondary'>
      {question}
    </Typography>
    <Stack
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      direction='row'
      spacing={1}
    >
      <Typography variant='subtitle2'>{value}</Typography>
      {state === 'confirmed' && (
        <Box sx={{ display: 'flex', color: 'custom.charcoal' }}>
          <Icon icon={'check' as never} fontSize={14} />
        </Box>
      )}
      {state === 'open' && (
        <Typography variant='caption' color='custom.coral.700'>
          Check
        </Typography>
      )}
    </Stack>
    {said && (
      <Typography
        sx={{ fontStyle: 'italic' }}
        variant='caption'
        color='text.secondary'
      >
        {saidPrefix ? `${saidPrefix}: ` : ''}“{said}”
      </Typography>
    )}
  </Stack>
);
ProposalRow.displayName = 'ProposalRow';

// Confirm / change, the pair every proposal terminates in. 16px apart.
export const ConfirmPair = ({
  confirm,
  change,
}: {
  confirm: string;
  change: string;
}) => (
  <Stack spacing={2}>
    <Button ariaLabel={confirm} isFullWidth>
      {confirm}
    </Button>
    <Button ariaLabel={change} variant='secondary' isFullWidth>
      {change}
    </Button>
  </Stack>
);
ConfirmPair.displayName = 'ConfirmPair';

// The eligibility panel, driven by the real ported engine rather than a fixture.
// Every frame that claims an answer moved the outcome renders this, so the claim
// is checkable: change the input and the list changes with it.
export const Results = ({
  input,
  limit = 5,
}: {
  input: ScreenerInput;
  limit?: number;
}) => {
  const evaluation = evaluate(input);
  const eligible = evaluation.eligibleIds.slice(0, limit);
  const unknown = evaluation.results.filter(
    (r) => r.status === 'more-info-needed',
  ).length;

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant='h3'>{TAP.results.title}</Typography>
        <Typography variant='body2' color='text.secondary'>
          {TAP.results.subtitle}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        {eligible.map((id) => {
          const benefit = BENEFITS[id];
          const range = valueRange(benefit.min, benefit.max);
          return (
            <Stack
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderRadius: `${radius.md}px`,
                border: 1,
                borderColor: 'custom.neutral.200',
                bgcolor: 'background.paper',
              }}
              key={id}
              direction='row'
              spacing={1}
            >
              <Stack sx={{ minWidth: 0 }} spacing={0.25}>
                <Typography variant='subtitle2'>{benefit.shortName}</Typography>
                {range && (
                  <Typography variant='caption' color='text.secondary'>
                    {range} {TAP.results.estimate}
                  </Typography>
                )}
              </Stack>
              <Pill label='Worth pursuing' tone={STATUS_TONE.eligible} />
            </Stack>
          );
        })}
      </Stack>

      {unknown > 0 && (
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
          <Pill
            label={`${unknown} ${TAP.results.moreInfo.toLowerCase()}`}
            tone={STATUS_TONE['more-info-needed']}
          />
        </Stack>
      )}
    </Stack>
  );
};
Results.displayName = 'Results';

// What a mode was actually worth: the engine run before and after its answers
// land, with the difference stated plainly including when there isn't one.
//
// This exists because the first version of the payoff frame claimed a win the
// engine did not produce. Measuring it in the component rather than writing the
// number into the copy is what stops that happening again.
export const Delta = ({
  before,
  after,
}: {
  before: Answers;
  after: Answers;
}) => {
  const run = (answers: Answers) => {
    const evaluation = evaluate(buildInput(answers, QUIZ_ORDER));
    return {
      names: evaluation.eligibleIds.map((id) => BENEFITS[id].shortName),
      value: totalEstimatedValue(evaluation),
    };
  };
  const from = run(before);
  const to = run(after);
  const appeared = to.names.filter((name) => !from.names.includes(name));
  const gained = to.value - from.value;

  const Column = ({
    heading,
    names,
    value,
  }: {
    heading: string;
    names: string[];
    value: number;
  }) => (
    <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.75}>
      <Typography variant='overline' color='text.secondary'>
        {heading}
      </Typography>
      <Typography variant='h3'>{names.length}</Typography>
      <Typography variant='caption' color='text.secondary'>
        {gbp(value)}
      </Typography>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Stack
        sx={{
          p: 2,
          borderRadius: `${radius.md}px`,
          border: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'background.paper',
        }}
        direction='row'
        spacing={2}
      >
        <Column heading={DELTA.before} names={from.names} value={from.value} />
        <Column heading={DELTA.after} names={to.names} value={to.value} />
      </Stack>

      <Note
        title={appeared.length > 0 ? DELTA.appeared : DELTA.nothingNew}
        tone={appeared.length > 0 ? 'working' : 'warn'}
        icon={appeared.length > 0 ? 'check' : 'circle-question'}
        body={
          appeared.length > 0
            ? appeared.join(', ')
            : 'Every answer was already covered by the questions before it.'
        }
      />

      <Typography variant='body2' color='text.secondary'>
        {gained > 0 ? `${gbp(gained)} ${DELTA.moreMoney}` : DELTA.noMoney}
      </Typography>
    </Stack>
  );
};
Delta.displayName = 'Delta';

// A thin strip at the top of a screen saying work is happening elsewhere. The
// whole async argument rests on this being unobtrusive: if it demands attention
// the user waits, and waiting is the thing the seam exists to avoid.
export const WorkingBanner = ({ label }: { label: string }) => (
  <Stack
    sx={{
      alignItems: 'center',
      px: 2,
      py: 1,
      borderRadius: `${radius.xxl}px`,
      bgcolor: 'custom.marineGreen.50',
    }}
    direction='row'
    spacing={1}
  >
    <Box sx={{ display: 'flex', color: 'text.secondary' }}>
      <Icon icon={'arrows-rotate' as never} fontSize={12} />
    </Box>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
  </Stack>
);
WorkingBanner.displayName = 'WorkingBanner';
